import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Typography, Card, Avatar, Tabs, List, Button, Rate,
  Spin, Empty, Tag, Form, Input, message,
} from 'antd';
import {
  UserOutlined, HeartOutlined, StarOutlined, SettingOutlined,
  EditOutlined, MailOutlined,
} from '@ant-design/icons';
import { AuthContext } from '../store/AuthContext';
import { useLanguage } from '../store/LanguageContext';
import { userAPI, authAPI } from '../services/api';
import { Tool, Review } from '../types';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, loading } = useContext(AuthContext);
  const { t } = useLanguage();

  const [favorites, setFavorites] = useState<Tool[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loadingFav, setLoadingFav] = useState(true);
  const [loadingReview, setLoadingReview] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [form] = Form.useForm();

  const fetchFavorites = useCallback(async () => {
    try {
      setLoadingFav(true);
      const res = await userAPI.getFavorites();
      setFavorites(res.data.favorites || []);
    } catch (error) {
      console.error('获取收藏失败:', error);
    } finally {
      setLoadingFav(false);
    }
  }, []);

  const fetchReviews = useCallback(async () => {
    try {
      setLoadingReview(true);
      const res = await userAPI.getReviews();
      setReviews(res.data.reviews || []);
    } catch (error) {
      console.error('获取评价失败:', error);
    } finally {
      setLoadingReview(false);
    }
  }, []);

  useEffect(() => {
    if (loading) {
      return;
    }
    if (!user) {
      navigate('/login');
      return;
    }
    fetchFavorites();
    fetchReviews();
  }, [user, loading, navigate, fetchFavorites, fetchReviews]);

  const handleUpdateProfile = async (values: { username: string }) => {
    try {
      setUpdating(true);
      await authAPI.updateProfile(values);
      message.success(t('profile.updateSuccess'));
      setEditMode(false);
      window.location.reload();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('profile.updateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    message.success(t('profile.logoutSuccess'));
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const tabItems = [
    {
      key: 'favorites',
      label: (
        <span>
          <HeartOutlined /> {t('profile.myFavorites')} ({favorites.length})
        </span>
      ),
      children: loadingFav ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : favorites.length > 0 ? (
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3 }}
          dataSource={favorites}
          renderItem={(tool: any) => {
            const t = tool.tool || tool;
            return (
              <List.Item>
                <Card
                  hoverable
                  onClick={() => navigate(`/tools/${t.slug}`)}
                  style={{ borderRadius: 12 }}
                >
                  <Card.Meta
                    avatar={
                      t.logo ? (
                        <Avatar src={t.logo} size={48} shape="square" style={{ borderRadius: 8 }} />
                      ) : (
                        <Avatar size={48} shape="square" style={{ borderRadius: 8, background: '#1677ff' }}>
                          {t.name?.charAt(0)}
                        </Avatar>
                      )
                    }
                    title={t.name}
                    description={
                      <>
                        <Rate disabled defaultValue={Number(t.rating) || 0} style={{ fontSize: 12 }} />
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t.short_description?.substring(0, 40)}...
                        </Text>
                      </>
                    }
                  />
                </Card>
              </List.Item>
            );
          }}
        />
      ) : (
        <Empty description={t('profile.noFavorites')}>
          <Button type="primary" onClick={() => navigate('/tools')}>
            {t('profile.discoverTools')}
          </Button>
        </Empty>
      ),
    },
    {
      key: 'reviews',
      label: (
        <span>
          <StarOutlined /> {t('profile.myReviews')} ({reviews.length})
        </span>
      ),
      children: loadingReview ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : reviews.length > 0 ? (
        <List
          dataSource={reviews}
          renderItem={(review: any) => (
            <List.Item style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 12 }}>
              <List.Item.Meta
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                      <Text
                        strong
                        style={{ cursor: 'pointer', color: '#1677ff' }}
                        onClick={() => navigate(`/tools/${review.tool?.slug}`)}
                      >
                        {review.tool?.name || t('profile.unknownTool')}
                      </Text>
                      <Rate disabled defaultValue={review.rating} style={{ fontSize: 14, marginLeft: 12 }} />
                    </span>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(review.created_at).format('YYYY-MM-DD')}
                    </Text>
                  </div>
                }
                description={
                  <>
                    {review.title && <Text strong>{review.title}</Text>}
                    <Paragraph style={{ margin: '4px 0 0', color: '#666' }}>
                      {review.comment}
                    </Paragraph>
                  </>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Empty description={t('profile.noReviews')}>
          <Button type="primary" onClick={() => navigate('/tools')}>
            {t('profile.exploreTools')}
          </Button>
        </Empty>
      ),
    },
    {
      key: 'settings',
      label: (
        <span>
          <SettingOutlined /> {t('profile.settings')}
        </span>
      ),
      children: (
        <Card style={{ borderRadius: 12, maxWidth: 500 }}>
          <Form
            form={form}
            layout="vertical"
            initialValues={{ username: user.username, email: user.email }}
            onFinish={handleUpdateProfile}
          >
            <Form.Item
              name="username"
              label={t('auth.usernameLabel')}
              rules={[{ required: true, message: t('auth.usernameRequired') }]}
            >
              <Input prefix={<UserOutlined />} disabled={!editMode} />
            </Form.Item>

            <Form.Item name="email" label={t('auth.emailLabel')}>
              <Input prefix={<MailOutlined />} disabled />
            </Form.Item>

            <Form.Item>
              {editMode ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button type="primary" htmlType="submit" loading={updating}>
                    {t('profile.save')}
                  </Button>
                  <Button onClick={() => setEditMode(false)}>{t('common.cancel')}</Button>
                </div>
              ) : (
                <Button icon={<EditOutlined />} onClick={() => setEditMode(true)}>
                  {t('profile.editProfile')}
                </Button>
              )}
            </Form.Item>
          </Form>

          <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #f0f0f0' }}>
            <Button danger onClick={handleLogout}>
              {t('profile.logout')}
            </Button>
          </div>
        </Card>
      ),
    },
  ];

  return (
    <div className="page-container" style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      {/* 用户信息卡片 */}
      <Card style={{ borderRadius: 16, marginBottom: 24 }}>
        <Row align="middle" gutter={24}>
          <Col>
            <Avatar size={72} icon={<UserOutlined />} style={{ background: '#1677ff' }}>
              {user.username.charAt(0).toUpperCase()}
            </Avatar>
          </Col>
          <Col>
            <Title level={3} style={{ margin: 0 }}>{user.username}</Title>
            <Text type="secondary">{user.email}</Text>
            <div style={{ marginTop: 8 }}>
              <Tag color={user.role === 'admin' ? 'red' : 'blue'}>
                {user.role === 'admin' ? t('profile.roleAdmin') : t('profile.roleUser')}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                {t('profile.joinedAt')} {dayjs(user.created_at).format('YYYY-MM-DD')}
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 标签页 */}
      <Tabs items={tabItems} size="large" />
    </div>
  );
};

export default ProfilePage;
